import { useEffect, useState } from "react";
import { useAuth, API } from "../App";
import { AlertTriangle, X, ChevronDown, ChevronUp } from "lucide-react";
import { HealthRerunButton, RERUNNABLE } from "./HealthRerunButton";

export const PipelineAlertBanner = () => {
  const { token } = useAuth();
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(
    sessionStorage.getItem("pipeline_alert_dismissed") === "1"
  );
  const [expanded, setExpanded] = useState(false);

  const load = (refresh) =>
    fetch(`${API}/pipeline/health${refresh ? "?refresh=true" : ""}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    load(false).then((d) => alive && setHealth(d));
    return () => {
      alive = false;
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!health || health.status === "ok" || dismissed) return null;

  const failing = [...(health.checks || []), ...(health.jobs || [])].filter(
    (x) => x.status !== "ok"
  );
  const isError = health.status === "error";
  const nEvents = (health.recent_errors || []).length;

  return (
    <div
      data-testid="pipeline-alert-banner"
      className={`border-b px-4 lg:px-6 py-2 text-sm ${
        isError
          ? "bg-red-950/60 border-red-800 text-red-200"
          : "bg-amber-950/60 border-amber-800 text-amber-200"
      }`}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="font-medium">
          Pipeline health: {isError ? "ERROR" : "WARNING"}
        </span>
        <span className="text-xs opacity-80 truncate">
          {failing.length > 0
            ? `${failing.length} failing check${failing.length > 1 ? "s" : ""} — ${failing
                .map((f) => f.label || f.id)
                .slice(0, 3)
                .join(", ")}${failing.length > 3 ? "…" : ""}`
            : `${nEvents} recent pipeline event${nEvents > 1 ? "s" : ""}`}
        </span>
        {failing
          .filter((f) => RERUNNABLE[f.id])
          .map((f) => (
            <span key={f.id} className="text-xs shrink-0" data-testid={`health-rerun-row-${f.id}`}>
              Re-run {RERUNNABLE[f.id]}
              <HealthRerunButton checkId={f.id} token={token} onDone={() => load(true).then(setHealth)} />
            </span>
          ))}
        <div className="flex-1" />
        {(failing.length > 0 || nEvents > 0) && (
          <button
            data-testid="pipeline-alert-expand"
            onClick={() => setExpanded((e) => !e)}
            className="p-1 hover:bg-white/10 rounded-sm shrink-0"
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        )}
        <button
          data-testid="pipeline-alert-dismiss"
          onClick={() => {
            sessionStorage.setItem("pipeline_alert_dismissed", "1");
            setDismissed(true);
          }}
          className="p-1 hover:bg-white/10 rounded-sm shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {expanded && (
        <div className="mt-2 ml-7 space-y-1 text-xs" data-testid="pipeline-alert-details">
          {failing.map((f) => (
            <div key={f.id}>
              <span className="font-medium">{f.label || f.id}:</span>{" "}
              <span className="opacity-80">{f.detail || f.status}</span>
            </div>
          ))}
          {(health.recent_errors || []).slice(0, 5).map((e, i) => (
            <div key={i} className="opacity-70">
              {e.date} [{e.source}/{e.job}] {String(e.detail || "").slice(0, 160)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
